// Сравнение двух версий документа: по блокам (абзацы, заголовки, подписи, формулы, таблицы), внутри
// изменённых абзацев - по словам. Используется в «Файл → Версии → Сравнить».
import type { JSONContent } from '@tiptap/core';
import { STRUCT_KINDS } from '../../core/schema/constants';

export interface Blk { kind: string; text: string }
export type Row =
  | { op: 'same'; a: Blk }
  | { op: 'del'; a: Blk }
  | { op: 'add'; b: Blk }
  | { op: 'mod'; a: Blk; b: Blk; words: WordOp[] };
export interface WordOp { op: 'same' | 'del' | 'add'; t: string }

function inlineText(n: JSONContent | undefined): string {
  if (!n) return '';
  if (n.type === 'text') return n.text || '';
  if (n.type === 'mathInline') return `$${n.attrs?.latex || ''}$`;
  if (n.type === 'cite') return '[' + ((n.attrs?.items || []) as any[]).map((i) => i.source).join('; ') + ']';
  if (n.type === 'xref') return '[ссылка]';
  if (n.type === 'footnote') return '[сноска]';
  if (n.type === 'hardBreak') return ' ';
  return (n.content || []).map(inlineText).join('');
}

const KIND: Record<string, string> = {
  paragraph: 'Абзац', heading: 'Заголовок', figure: 'Рисунок', stoTable: 'Таблица', formula: 'Формула', listing: 'Листинг',
  note: 'Примечание', example: 'Пример', pageBreak: 'Разрыв', structHeading: 'Элемент', appendixTitle: 'Приложение',
};

/** Плоский список блоков документа (JSON TipTap). */
export function flatten(doc: JSONContent | undefined): Blk[] {
  const out: Blk[] = [];
  const walk = (n: JSONContent) => {
    const t = n.type || '';
    if (t === 'paragraph' || t === 'heading' || t === 'appendixTitle') {
      const text = inlineText(n);
      if (text.trim() || t !== 'paragraph') out.push({ kind: t === 'heading' ? `Заголовок ${n.attrs?.level || 1}` : KIND[t], text });
    } else if (t === 'structHeading') out.push({ kind: KIND[t], text: STRUCT_KINDS[n.attrs?.kind] || n.attrs?.kind });
    else if (t === 'figure') out.push({ kind: KIND[t], text: inlineText(n) + (n.attrs?.src ? ` [${n.attrs.src}]` : '') });
    else if (t === 'formula') out.push({ kind: KIND[t], text: n.attrs?.latex || '' });
    else if (t === 'listing') out.push({ kind: KIND[t], text: inlineText(n) + ' · ' + String(n.attrs?.code || '').slice(0, 400) });
    else if (t === 'stoTable') {
      const cap = (n.content || []).find((c) => c.type === 'caption');
      out.push({ kind: KIND[t], text: inlineText(cap) });
      const tbl = (n.content || []).find((c) => c.type === 'table');
      for (const row of tbl?.content || []) out.push({ kind: 'Строка таблицы', text: (row.content || []).map(inlineText).join(' | ') });
    } else if (t === 'pageBreak') out.push({ kind: KIND[t], text: '' });
    else (n.content || []).forEach(walk);
  };
  (doc?.content || []).forEach(walk);
  return out;
}

/** Наибольшая общая подпоследовательность: пары индексов совпадающих элементов. */
function lcs<T>(a: T[], b: T[], eq: (x: T, y: T) => boolean): [number, number][] {
  let s = 0;
  while (s < a.length && s < b.length && eq(a[s], b[s])) s++;
  let e = 0;
  while (e < a.length - s && e < b.length - s && eq(a[a.length - 1 - e], b[b.length - 1 - e])) e++;
  const A = a.slice(s, a.length - e); const B = b.slice(s, b.length - e);
  const n = A.length; const m = B.length;
  const pairs: [number, number][] = [];
  for (let i = 0; i < s; i++) pairs.push([i, i]);
  if (n && m && n * m <= 16e6) {
    const dp = new Uint32Array((n + 1) * (m + 1));
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        dp[i * (m + 1) + j] = eq(A[i], B[j]) ? dp[(i + 1) * (m + 1) + j + 1] + 1 : Math.max(dp[(i + 1) * (m + 1) + j], dp[i * (m + 1) + j + 1]);
      }
    }
    let i = 0; let j = 0;
    while (i < n && j < m) {
      if (eq(A[i], B[j])) { pairs.push([s + i, s + j]); i++; j++; }
      else if (dp[(i + 1) * (m + 1) + j] >= dp[i * (m + 1) + j + 1]) i++;
      else j++;
    }
  }
  for (let k = 0; k < e; k++) pairs.push([a.length - e + k, b.length - e + k]);
  return pairs;
}

export function wordDiff(a: string, b: string): WordOp[] {
  const A = a.split(/(\s+)/).filter(Boolean);
  const B = b.split(/(\s+)/).filter(Boolean);
  const pairs = lcs(A, B, (x, y) => x === y);
  const out: WordOp[] = [];
  let i = 0; let j = 0;
  const push = (op: WordOp['op'], t: string) => { const l = out[out.length - 1]; if (l && l.op === op) l.t += t; else out.push({ op, t }); };
  for (const [pi, pj] of [...pairs, [A.length, B.length] as [number, number]]) {
    while (i < pi) push('del', A[i++]);
    while (j < pj) push('add', B[j++]);
    if (pi < A.length) { push('same', A[pi]); i = pi + 1; j = pj + 1; }
  }
  return out;
}

/** Сравнение версий: a - старая, b - новая. */
export function diffDocs(a: JSONContent | undefined, b: JSONContent | undefined): Row[] {
  const A = flatten(a); const B = flatten(b);
  const pairs = lcs(A, B, (x, y) => x.kind === y.kind && x.text === y.text);
  const rows: Row[] = [];
  let i = 0; let j = 0;
  for (const [pi, pj] of [...pairs, [A.length, B.length] as [number, number]]) {
    const dels = A.slice(i, pi); const adds = B.slice(j, pj);
    // удалённый + добавленный блок одного вида подряд - «изменён» (с разницей по словам)
    const k = Math.min(dels.length, adds.length);
    for (let q = 0; q < k; q++) {
      if (dels[q].kind === adds[q].kind) rows.push({ op: 'mod', a: dels[q], b: adds[q], words: wordDiff(dels[q].text, adds[q].text) });
      else { rows.push({ op: 'del', a: dels[q] }); rows.push({ op: 'add', b: adds[q] }); }
    }
    for (let q = k; q < dels.length; q++) rows.push({ op: 'del', a: dels[q] });
    for (let q = k; q < adds.length; q++) rows.push({ op: 'add', b: adds[q] });
    if (pi < A.length) rows.push({ op: 'same', a: A[pi] });
    i = pi + 1; j = pj + 1;
  }
  return rows;
}

export function diffStats(rows: Row[]) {
  let add = 0; let del = 0; let mod = 0;
  for (const r of rows) { if (r.op === 'add') add++; else if (r.op === 'del') del++; else if (r.op === 'mod') mod++; }
  return { add, del, mod };
}
